let SELECTABLE_THROTTLING_TIMEOUT = 300;
export default {
	data() {
		return {
			selected: {},
			lastSelected: {},
			selectableThrottling: false,
			clickTimeouts: {},
			focusedIndex: -1
		};
	},
	props: {
		/**
		 * Should rows be selectable
		 */
		selectable: Boolean,

		/**
		 * Select mode of row
		 */
		selectableMode: {
			type: Array,
			default: () => [ 'checkbox', 'row' ]
		},

		/**
		 * Disable being able to tab to checkboxes for selecting rows. Sets tabindex to -1
		 */
		disableSelectCheckboxTabbing: {
			type: Boolean,
			default: false
		},

		/**
		 * Selected items
		 */
		selectedIds: {
			type: Array,
			default: () => []
		},

		/**
		 * Should selected items be deselected if they are filtered out?
		 */
		keepFilterdOutItems: {
			type: Boolean,
			default: false
		},
		/**
		 * Should selection allow only one selection?
		 */
		singleSelect: {
			type: Boolean,
			default: false
		}
	},
	computed: {
		checkboxVisible() {
			return this.selectable && this.selectableMode.indexOf('checkbox') >= 0;
		},
		allSelected() {
			return this.selectedCount === this.filteredItems.length;
		},
		someSelected() {
			return this.selectedCount > 0 && this.selectedCount < this.filteredItems.length;
		},
		selectedCount() {
			return Object.keys(this.selected).length;
		}
	},
	methods: {
		_resetSelectedRows() {
			this.$set(this, 'selected', {});
			this.$emit('selected',[]);
		},
		_selectRow(item, event) {
			if (event && event.ctrlKey || event.metaKey) {
				this._addSingleRow(item);
			} else if (event && event.shiftKey) {
				this._addMultipleRows(item);
			} else {
				this._selectSingleRow(item);
			}

			this.$emit('selected',Object.keys(this.selected));
			this.lastSelected = item;
		},
		_selectSingleRow(item) {
			if (this._isSelected(item) && this.selectedCount === 1)
				this.$delete(this.selected, item[this.idKey]);
			else {
				this._resetSelectedRows();
				this.$set(this.selected, item[this.idKey], item);
				this.focusedIndex = this.sortedItems.indexOf(item);
			}
		},
		_addSingleRow(item) {
			if (this._isSelected(item))
				this.$delete(this.selected, item[this.idKey]);
			else
				this.$set(this.selected, item[this.idKey], item);
		},
		_isSelected(item) {
			return this.selected.hasOwnProperty(item[this.idKey]);
		},
		_addMultipleRows(item) {
			if (!this.lastSelected  || this.selectedCount <= 0) return;
			let lastSelectedIndex = this.sortedItems.indexOf(this.lastSelected);
			let itemIndex = this.sortedItems.indexOf(item);
			this.focusedIndex = itemIndex;
			let startIndex = Math.min(lastSelectedIndex, itemIndex);
			let stopIndex = Math.max(lastSelectedIndex, itemIndex);
			for (let i = startIndex; i <= stopIndex; i++) {
				this.$set(this.selected, this.sortedItems[i][this.idKey], this.sortedItems[i]);
			}
		},
		_onSingleClick(event,item) {
			if (this.selectableMode.indexOf('row') === -1) return;
			if (this.selectableThrottling) return;
			if (this.selectable) {
				this._selectRow(item, event);
				this.selectableThrottling = true;
				setTimeout(() => {
					this.selectableThrottling = false;
				},SELECTABLE_THROTTLING_TIMEOUT);
			}
		},
		_onRowClicked(event, item) {
			clearTimeout(this.clickTimeouts[item[this.idKey]]);
			this.clickTimeouts[item[this.idKey]] = setTimeout(() => {
				this._onSingleClick(event,item);
			},200);
		},
		_onCheckboxClicked(item) {
			if (this.selectableThrottling) return;
			if (this.selectable) {
				this._selectRow(item, { metaKey: true, ctrlKey: true });
				this.selectableThrottling = true;
				setTimeout(() => {
					this.selectableThrottling = false;
				},SELECTABLE_THROTTLING_TIMEOUT);
			}
		},
		_onRowDoubleClicked(item) {
			if (!item) return;
			clearTimeout(this.clickTimeouts[item[this.idKey]]);
			this.$emit('rowDoubleClick', item[this.idKey]);
		},
		_onSelectAllClicked() {
			if (!this.selectable) return;
			if (this.selectedCount === this.filteredItems.length) {
				this.$set(this, 'selected', {});
			} else {
				let result = {};
				for (let item of this.filteredItems)
					result[item[this.idKey]] = item;
				this.$set(this, 'selected', result);
			}
			this.$emit('selected',Object.keys(this.selected));
		},
		_selectableOnKeyDown(e) {
			if (e.target.nodeName === 'INPUT' || e.target.nodeName === 'TEXTAREA') return;
			let delta = { x: 0, y: 0 };
			switch (e.key) {
			case 'PageUp':
			case 'PageDown':
				delta.y = this._handlePageUpOrDown(e.key);
				break;

			case 'ArrowUp':
			case 'ArrowDown':
				this._handleUpOrDown(e.key);
				break;

			case 'Home':
			case 'End':
				delta.y = (e.key === 'Home') ? -Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
				break;

			case 'ArrowLeft':
			case 'ArrowRight':
				delta.x = (e.key === 'ArrowLeft') ? -this.rowHeight : this.rowHeight;
				break;

			case 'Enter':
				this._handleRowEnter();
				break;
			default:
				return;
			}
			e.preventDefault();
			this.targetScroller.scrollTop += delta.y;
			this.$refs.gridInnerContainer.scrollLeft += delta.x;
		},
		_handlePageUpOrDown(key) {
			let containerHeight = this.$refs.gridContainer.offsetHeight;
			let delta = (containerHeight - 70 - this.rowHeight);
			if (key === 'PageUp')
				delta *= -1;
			return delta;
		},
		_handleUpOrDown(key) {
			if (key === 'ArrowUp') {
				this.focusedIndex = (this.focusedIndex - 1) % this.filteredItems.length;
				if (this.focusedIndex < 0)
					this.focusedIndex = this.filteredItems.length - 1;
			} else {
				this.focusedIndex = (this.focusedIndex + 1) % this.filteredItems.length;
			}
			if (this.useRecycleScroller)
				this.$refs.recycleScroller.forceRender();
		},
		_handleRowEnter() {
			let item = this.sortedItems[this.focusedIndex];
			this._onRowDoubleClicked(item);
		},
	},
	watch: {
		selectedIds: {
			handler(newValue, oldValue) {
				if (newValue === oldValue) return;
				let selected = {};
				for (let id of newValue) {
					selected[id] = this.items.find(item => item[this.idKey] === id);
				}
				this.$set(this, 'selected',  selected);
			},
			immediate: true
		},
		filteredItems(newValue, oldValue) {
			if (newValue === oldValue) return;
			if (this.keepFilterdOutItems) return;
			for (let id in this.selected) {
				if (!newValue.some(x => x.id === id))
					this.$delete(this.selected, id);
			}
			this.focusedIndex = -1;
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.$refs.gridContainer.addEventListener('keydown', this._selectableOnKeyDown, { capture: true });
		});
	},
	beforeDestroy() {
		this.$refs.gridContainer.removeEventListener('keydown', this._selectableOnKeyDown, { capture: true });
	}
};